import { ReactNode } from "react";
import { Box, Typography, useTheme } from "@mui/material";

// ----------------------------------------------------------------------

interface AuthShellProps {
  /** Rótulo pequeno dourado acima do título (ex.: "Bem-vinda de volta") */
  kicker?: string;
  /** Título editorial (DM Serif) do painel do formulário */
  title: string;
  /** Conteúdo abaixo do título (ex.: link "Inscreva-se") */
  subtitle?: ReactNode;
  /** O formulário */
  children: ReactNode;
  /** Pill do painel espresso (default: "Home care personalizado"). null oculta. */
  panelBadge?: string | null;
  /** Manchete DM Serif do painel espresso */
  panelHeadline?: string;
  /** Texto de apoio do painel espresso (null oculta) */
  panelText?: string | null;
}

const DEFAULT_BADGE = "Home care personalizado";
const DEFAULT_HEADLINE = "Ciência e cuidado em cada fórmula que você prescreve.";
const DEFAULT_TEXT = "Mais de 570 ativos, gestão de pacientes e receitas em PDF — tudo em um só lugar.";

const Hexagon = ({ sx }: { sx: object }) => (
  <Box
    component="svg"
    viewBox="0 0 100 110"
    fill="none"
    sx={{ position: "absolute", pointerEvents: "none", ...sx }}
  >
    <polygon
      points="2,55 26,13.4 74,13.4 98,55 74,96.6 26,96.6"
      stroke="#E9D9BC"
      strokeWidth="2"
    />
  </Box>
);

/**
 * Shell editorial das telas de autenticação (fiel ao handoff "split editorial"):
 * painel espresso à esquerda (logo + manchete + hexágono) e painel de formulário
 * à direita. No mobile o painel vira um cabeçalho compacto no topo.
 */
export default function AuthShell({
  kicker,
  title,
  subtitle,
  children,
  panelBadge = DEFAULT_BADGE,
  panelHeadline = DEFAULT_HEADLINE,
  panelText = DEFAULT_TEXT,
}: AuthShellProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 0, md: 4 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { md: 1080 },
          minHeight: { xs: "100vh", md: 640 },
          bgcolor: "background.paper",
          borderRadius: { xs: 0, md: "24px" },
          overflow: "hidden",
          border: { md: `1px solid ${theme.palette.divider}` },
          boxShadow: { md: theme.customShadows.card },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Painel espresso */}
        <Box
          sx={{
            position: "relative",
            width: { xs: "100%", md: "47%" },
            minHeight: { xs: 210, md: "auto" },
            background: theme.palette.brand.authPanel,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: { xs: "26px 24px 40px", md: "44px" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0 2px, transparent 2px 16px)",
              opacity: 0.7,
            }}
          />
          <Hexagon sx={{ right: -60, top: 60, width: 280, height: 300, opacity: 0.13, display: { xs: "none", md: "block" } }} />
          <Hexagon sx={{ left: -40, bottom: -30, width: 200, height: 220, opacity: 0.1, display: { xs: "none", md: "block" } }} />

          <Box sx={{ position: "relative", display: "flex", justifyContent: { xs: "center", md: "flex-start" } }}>
            <Box
              component="img"
              src="/imgs/newLogoWhite.svg"
              alt="ProAtivos"
              sx={{ height: { xs: 26, md: 34 }, width: "auto", opacity: 0.96 }}
            />
          </Box>

          <Box sx={{ position: "relative" }}>
            {panelBadge && (
              <Box
                sx={{
                  display: { xs: "none", md: "inline-block" },
                  px: "12px",
                  py: "6px",
                  mb: "22px",
                  border: "1px solid rgba(233,217,188,0.4)",
                  borderRadius: "100px",
                  fontSize: "11px",
                  letterSpacing: "1.4px",
                  textTransform: "uppercase",
                  color: "#E9D9BC",
                }}
              >
                {panelBadge}
              </Box>
            )}
            <Typography
              sx={{
                fontFamily: theme.typography.fontFamilyDisplay,
                fontStyle: "italic",
                fontWeight: 500,
                fontSize: { xs: "24px", md: "40px" },
                lineHeight: 1.15,
                color: "#FCF6EA",
                maxWidth: 380,
              }}
            >
              {panelHeadline}
            </Typography>
            {panelText && (
              <Typography
                sx={{
                  display: { xs: "none", md: "block" },
                  fontSize: "14px",
                  color: "rgba(252,246,234,0.72)",
                  mt: "18px",
                  maxWidth: 340,
                  lineHeight: 1.6,
                }}
              >
                {panelText}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Painel do formulário */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            p: { xs: "28px 24px 36px", md: "56px 64px" },
            mt: { xs: "-22px", md: 0 },
            bgcolor: "background.paper",
            borderRadius: { xs: "24px 24px 0 0", md: 0 },
            position: "relative",
          }}
        >
          <Box sx={{ maxWidth: 380, width: "100%", mx: "auto" }}>
            {kicker && (
              <Typography
                sx={{
                  fontSize: "12px",
                  letterSpacing: "1.4px",
                  textTransform: "uppercase",
                  color: "primary.main",
                  fontWeight: 600,
                  mb: "10px",
                }}
              >
                {kicker}
              </Typography>
            )}
            <Typography
              sx={{
                fontFamily: theme.typography.fontFamilyDisplay,
                fontSize: { xs: "28px", md: "40px" },
                fontWeight: 600,
                color: "text.primary",
                lineHeight: 1.1,
                mb: subtitle ? "8px" : "28px",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Box sx={{ mb: "32px", fontSize: "14px", color: "text.secondary" }}>{subtitle}</Box>
            )}
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
